import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ZonedDateTime } from '@internationalized/date';
import { AuthContext } from '@lp/core/auth';
import { useRouteModals } from '@lp/features/RoutePlanner/hooks/useRouteModals';
import { useResourcesHandlers } from '@lp/features/RoutePlanner/hooks/useResourcesHandlers';
import {
  getLogisticsDataById,
  getResourceEmployeeData,
  getResourceOscDeviceData,
} from '@lp/features/RoutePlanner/utils';
import { RouteModalTypes } from '@lp/features/RoutePlanner/types';
import { perfect } from '@lp/features/AssignmentTables/components/ReservationCondition/utils';
import { ConfirmationCondition } from '@lp/features/AssignmentTables/components';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { useLogisticsData } from '@lp/features/RoutePlanner/hooks/useLogisticsProjection';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import {
  CSAssignmentCandidate,
  Employee,
  EmployeeAssignmentCandidateMatch,
  EmployeeComposed,
  EmployeeRole,
  LastCertificationsAtHubInfo,
  OcsConsoleData,
  RSAssignmentCandidate,
  RSContractorAssignmentCandidate,
} from '@/api/apis/resources';
import {
  CaseClinicalSpecialistResource,
  CaseFacility,
  CaseOCSConsoleResource,
  CaseResource,
  CaseResourceType,
  CaseRetrievalSurgeonResource,
  CaseStatus,
  RoutePlanStatus,
} from '@/api/apis/case';
import { TrialProgram } from '@/api/apis/facilities/trial-programs';
import { LastCertificationAtHubStatusType, Uuid } from '@/api/types';
import { RouteBuilderPermissions } from '@/permissions';
import { isAllowedStatus } from '@/utils/helpers';
import { useCaseDetailsWithReviews } from './useCaseDetailsWithReviews';
import { RouteTypeEnum } from '@/api/apis/route';
import { useRemoveResource } from '@/queries/route-planner';

const RESOURCE_ID_KEY_MAP: Record<string, string> = {
  [CaseResourceType.ClinicalSpecialist]: 'clinicalSpecialistIds',
  [CaseResourceType.OCSConsole]: 'ocsConsoleIds',
  [CaseResourceType.Surgeon]: 'surgeonIds',
};

type CurrentResourceType =
  | (CSAssignmentCandidate & { id: string })
  | (RSContractorAssignmentCandidate & { id: string })
  | (RSAssignmentCandidate & { id: string })
  | OcsConsoleData;

type UseResourceProps = {
  type: CaseResourceType.ClinicalSpecialist | CaseResourceType.Surgeon | CaseResourceType.OCSConsole;
  id: string;
  donorHospital?: CaseFacility;
  txCenter?: CaseFacility;
  applyGoTime?: boolean;
};

type CSWithTrialIds = CSAssignmentCandidate & { trialProgramIds: Uuid[] };

const hasTrialIds = (cs: CSAssignmentCandidate): cs is CSWithTrialIds => {
  return (
    Object.prototype.hasOwnProperty.call(cs, 'trialProgramIds') && Array.isArray((cs as CSWithTrialIds).trialProgramIds)
  );
};

const hasCsTrialCertification = (cs: CSAssignmentCandidate, tp?: TrialProgram | null): boolean => {
  if (!tp) return true;

  const hasViaOrgan = Boolean(tp.organ) && Boolean(cs.employeeTrialCertificates?.includes(tp.organ));
  const ids = hasTrialIds(cs) ? cs.trialProgramIds : undefined;
  const hasViaProgramId = Boolean(ids?.includes(tp.id));

  return hasViaOrgan || hasViaProgramId;
};

const hasMayBecomeLastCertificationAtHub = (lastCertInfo?: LastCertificationsAtHubInfo) => {
  return (
    !!lastCertInfo &&
    Object.values(lastCertInfo).some(
      (info) => info?.lastCertificationAtHubStatus === LastCertificationAtHubStatusType.NotLastButBecomeLast,
    )
  );
};

export const useResource = ({ type, id, donorHospital, applyGoTime }: UseResourceProps) => {
  const { checkPermission } = useContext(AuthContext);
  const { resolveSubdivisionName } = useCountriesData();
  const { caseId, organType, routePlanningStatus, status, trialProgram } = useCaseDetailsWithReviews();
  const { id: logisticsId } = useLogisticsData();
  const { legs: mainLegs } = useRouteDetails(RouteTypeEnum.Main);
  const { legs: dryRunLegs } = useRouteDetails(RouteTypeEnum.DryRun);
  const { onOpenModal, setIsLoading, isLoading, onCloseModal } = useRouteModals();
  const { [type]: resourceList } = useRouteResources();
  const { handleResourceAssignment, handleSwitchResources } = useResourcesHandlers({ id: caseId });
  const { mutateAsync: removeResource } = useRemoveResource();
  const resource = resourceList.find((r) => r.resourceId === id);
  const currentResource = resource?.data as CurrentResourceType | null;
  const [selectedResource, setSelectedResource] = useState<CurrentResourceType | null>(currentResource || null);

  const canReserveOrRemoveResources = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.AssignResource);
    const isCorrectStatus = routePlanningStatus === RoutePlanStatus.RoutePlanningStart;
    const isActiveCaseStatus = isAllowedStatus({
      statusFrom: CaseStatus.PendingLogistics,
      statusTo: CaseStatus.ReturningToBase,
      currentStatus: status,
    });

    return hasPermission && isCorrectStatus && isActiveCaseStatus;
  }, [status, checkPermission, routePlanningStatus]);

  const canAssignResources = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.AssignResource);
    const isActiveCaseStatus = isAllowedStatus({
      statusFrom: CaseStatus.PendingLogistics,
      statusTo: CaseStatus.ReturningToBase,
      currentStatus: status,
    });

    return hasPermission && isActiveCaseStatus;
  }, [status, checkPermission]);

  const hasResourceInLegs = useMemo(() => {
    if (!resource) return false;

    const hasMainLegs = mainLegs.some((leg) => leg.resources.some((r) => r.resourceId === resource.resourceId));
    const hasDryRunLegs =
      resource.resourceType !== CaseResourceType.Surgeon &&
      dryRunLegs.some((leg) => leg.resources.some((r) => r.resourceId === resource.resourceId));

    return hasMainLegs || hasDryRunLegs;
  }, [resource, mainLegs, dryRunLegs]);

  const resourceData = useMemo(() => {
    switch (type) {
      case CaseResourceType.ClinicalSpecialist: {
        const csData = getResourceEmployeeData({
          defaultTitle: 'Clinical Specialist',
          resource,
          resourceData: (resource?.data || selectedResource) as Employee | EmployeeComposed | null,
          type: EmployeeRole.ClinicalSpecialist,
          resolveSubdivisionName,
        });

        return {
          ...csData,
          showAssignButton: canReserveOrRemoveResources && !csData.isAssigned,
        };
      }
      case CaseResourceType.OCSConsole: {
        const ocsData = getResourceOscDeviceData({
          defaultTitle: 'Device',
          resource,
          resourceData: (resource?.data || selectedResource) as OcsConsoleData | null,
        });

        return {
          ...ocsData,
          showAssignButton: canReserveOrRemoveResources && !ocsData.isAssigned,
        };
      }
      case CaseResourceType.Surgeon: {
        const sData = getResourceEmployeeData({
          defaultTitle: 'Surgeon',
          resource,
          resourceData: (resource?.data || selectedResource) as Employee | EmployeeComposed | null,
          type:
            resource?.data &&
            (resource?.data as EmployeeComposed).isContractor &&
            resource?.resourceData?.roles &&
            resource?.resourceData?.roles.includes(EmployeeRole.ContractorRetrievalSurgeon)
              ? EmployeeRole.ContractorRetrievalSurgeon
              : EmployeeRole.RetrievalSurgeon,
          resolveSubdivisionName,
        });

        return {
          ...sData,
          showAssignButton: canReserveOrRemoveResources && !sData.isAssigned,
        };
      }
      default: {
        const defaultData = getResourceOscDeviceData({
          defaultTitle: 'Resource',
        });

        return {
          ...defaultData,
          showAssignButton: canReserveOrRemoveResources,
        };
      }
    }
  }, [resource, selectedResource, type, canReserveOrRemoveResources, resolveSubdivisionName]);

  const onSelect = useCallback(
    async (
      data: CSAssignmentCandidate | RSContractorAssignmentCandidate | RSAssignmentCandidate | OcsConsoleData,
      isOCSConsole?: boolean,
    ) => {
      try {
        setIsLoading(true);
        const resourceId = isOCSConsole
          ? (data as OcsConsoleData).id
          : (data as CSAssignmentCandidate | RSContractorAssignmentCandidate | RSAssignmentCandidate).employeeId;
        await handleResourceAssignment({ data: { [RESOURCE_ID_KEY_MAP[type]]: [resourceId] }, type: 'reserve' });

        onCloseModal();
      } catch (e) {
        setSelectedResource(null);
      } finally {
        setIsLoading(false);
      }
    },
    [handleResourceAssignment, onCloseModal, setIsLoading, type],
  );

  const onResourceRemove = useCallback(
    async (removeType: 'remove' | 'swap' = 'remove', newResourceId?: string) => {
      if (!currentResource) return;

      try {
        setIsLoading(true);

        if (removeType === 'swap' && newResourceId) {
          await handleSwitchResources(currentResource.id, newResourceId, type);
          toast.success('Resource was swapped.');
        }

        if (removeType === 'remove') {
          if (hasResourceInLegs) {
            const { version } = getLogisticsDataById(logisticsId);
            await removeResource({ logisticsId, resourceId: currentResource.id, version });
          }

          await handleResourceAssignment({
            data: { [RESOURCE_ID_KEY_MAP[type]]: [currentResource.id] },
            type: 'remove',
          });

          if (hasResourceInLegs) {
            toast.success('Resource and resource specific legs were removed.');
          } else {
            toast.success('Resource was removed.');
          }
        }

        setSelectedResource(null);
      } catch (e) {
        setSelectedResource(currentResource);
      } finally {
        setIsLoading(false);
        onCloseModal();
      }
    },
    [
      currentResource,
      setIsLoading,
      handleSwitchResources,
      type,
      handleResourceAssignment,
      removeResource,
      logisticsId,
      hasResourceInLegs,
      onCloseModal,
    ],
  );

  const onResourceAssign = useCallback(async () => {
    if (!currentResource) return;

    try {
      setIsLoading(true);

      const resourceKey = type === CaseResourceType.OCSConsole ? 'ocsConsoleIds' : 'employeeIds';
      await handleResourceAssignment({ data: { [resourceKey]: [currentResource.id] }, type: 'book' });

      toast.success('Resource was assigned.');
    } finally {
      setIsLoading(false);
      onCloseModal();
    }
  }, [currentResource, setIsLoading, type, handleResourceAssignment, onCloseModal]);

  useEffect(() => {
    setSelectedResource(currentResource || null);
  }, [currentResource]);

  const handleCSConfirmation = useCallback(
    (data: CSAssignmentCandidate, cb: () => void, goDateTime?: ZonedDateTime) => {
      const {
        employeeFullName,
        match,
        onCallAfterTargetMinutes,
        onCallBeforeTargetMinutes,
        onRestEndsBeforeTargetMinutes,
        onRestStartsAfterTargetMinutes,
        lastCertificationAtHubInfo,
        employeeTrialCertificates,
      } = data;

      const trialProgramIds = hasTrialIds(data) ? data.trialProgramIds : undefined;

      const hasMayBecomeLast = hasMayBecomeLastCertificationAtHub(lastCertificationAtHubInfo);
      const lacksTrialCert = !hasCsTrialCertification(data, trialProgram);
      const shouldOpenModal = !perfect(match.matchType) || lacksTrialCert || hasMayBecomeLast;

      if (shouldOpenModal) {
        onOpenModal({
          type: RouteModalTypes.ConfirmReservation,
          context: {
            title: 'Reserve Clinical Specialist',
            description: (
              <ConfirmationCondition
                goTime={goDateTime?.toAbsoluteString()}
                applyGoTime={applyGoTime}
                match={match}
                onCallAfterTargetMinutes={onCallAfterTargetMinutes}
                onCallBeforeTargetMinutes={onCallBeforeTargetMinutes}
                onRestEndsBeforeTargetMinutes={onRestEndsBeforeTargetMinutes}
                onRestStartsAfterTargetMinutes={onRestStartsAfterTargetMinutes}
                employeeRole={EmployeeRole.ClinicalSpecialist}
                userName={employeeFullName}
                lastCertificationAtHubInfo={lastCertificationAtHubInfo}
                trialProgram={trialProgram}
                employeeTrialCertificates={employeeTrialCertificates}
                trialProgramIds={trialProgramIds}
                hasTrialProgramCertification={!lacksTrialCert}
              />
            ),
            onSelect: cb,
          },
        });
      } else {
        cb();
      }
    },
    [trialProgram, onOpenModal, applyGoTime],
  );

  const handleSConfirmation = useCallback(
    (item: RSContractorAssignmentCandidate | RSAssignmentCandidate, cb: () => void) => {
      const { employeeFullName, match } = item;
      if (!perfect(match.matchType)) {
        onOpenModal({
          type: RouteModalTypes.ConfirmReservation,
          context: {
            title: 'Assign Retrieval Surgeon',
            description: (
              <ConfirmationCondition
                match={match as EmployeeAssignmentCandidateMatch}
                employeeRole={EmployeeRole.RetrievalSurgeon}
                userName={employeeFullName}
              />
            ),
            onSelect: cb,
          },
        });
      } else {
        cb();
      }
    },
    [onOpenModal],
  );

  const handleAssignModal = useCallback(() => {
    switch (type) {
      case CaseResourceType.ClinicalSpecialist:
        if (donorHospital) {
          onOpenModal({
            type: RouteModalTypes.AssignSpecialist,
            context: {
              organType,
              onAction: handleCSConfirmation,
              onConfirmation: onSelect,
            },
          });
        }
        break;
      case CaseResourceType.Surgeon:
        if (donorHospital) {
          onOpenModal({
            type: RouteModalTypes.AssignSurgeon,
            context: {
              organType,
              hideControls: false,
              onAction: handleSConfirmation,
              onConfirmation: onSelect,
            },
          });
        }
        break;
      case CaseResourceType.OCSConsole:
        if (donorHospital) {
          onOpenModal({
            type: RouteModalTypes.AssignDevice,
            context: {
              organType,
              onSelect,
            },
          });
        }
        break;
      default:
        break;
    }
  }, [onSelect, donorHospital, onOpenModal, handleCSConfirmation, handleSConfirmation, organType, type]);

  const handleResourceRemoveModal = useCallback(() => {
    switch (type) {
      case CaseResourceType.ClinicalSpecialist:
        if (hasResourceInLegs) {
          onOpenModal({
            type: RouteModalTypes.RemoveCS,
            context: {
              organType,
              resource: resource as CaseResource<CaseClinicalSpecialistResource>,
              onAction: handleCSConfirmation,
              onConfirmation: onResourceRemove,
            },
          });
        } else {
          onOpenModal({
            type: RouteModalTypes.RemoveResource,
            context: {
              resourceId: resource!.resourceId,
              onRemove: () => onResourceRemove(),
            },
          });
        }
        break;
      case CaseResourceType.Surgeon:
        onOpenModal({
          type: RouteModalTypes.RemoveResource,
          context: {
            resourceId: resource!.resourceId,
            onRemove: () => onResourceRemove(),
          },
        });
        break;
      case CaseResourceType.OCSConsole:
        if (hasResourceInLegs) {
          onOpenModal({
            type: RouteModalTypes.RemoveDevice,
            context: {
              organType,
              resource: resource as CaseResource<CaseOCSConsoleResource>,
              onRemove: onResourceRemove,
            },
          });
        } else {
          onOpenModal({
            type: RouteModalTypes.RemoveResource,
            context: {
              resourceId: resource!.resourceId,
              onRemove: () => onResourceRemove(),
            },
          });
        }
        break;
      default:
        break;
    }
  }, [type, hasResourceInLegs, onOpenModal, organType, resource, handleCSConfirmation, onResourceRemove]);

  const handleViewResourceModal = useCallback(() => {
    switch (type) {
      case CaseResourceType.ClinicalSpecialist:
        onOpenModal({
          type: RouteModalTypes.ViewSpecialist,
          context: {
            isAssignEnabled: canAssignResources,
            isRemoveEnabled: canReserveOrRemoveResources,
            resource: resource as CaseResource<CaseClinicalSpecialistResource>,
            onRemove: handleResourceRemoveModal,
            onAssign: onResourceAssign,
          },
        });
        break;
      case CaseResourceType.Surgeon:
        onOpenModal({
          type: RouteModalTypes.ViewSurgeon,
          context: {
            isAssignEnabled: canAssignResources,
            isRemoveEnabled: canReserveOrRemoveResources,
            resource: resource as CaseResource<CaseRetrievalSurgeonResource>,
            onRemove: handleResourceRemoveModal,
            onAssign: onResourceAssign,
          },
        });
        break;
      case CaseResourceType.OCSConsole:
        onOpenModal({
          type: RouteModalTypes.ViewDevice,
          context: {
            isAssignEnabled: canAssignResources,
            isRemoveEnabled: canReserveOrRemoveResources,
            resource: (resource as CaseResource<CaseOCSConsoleResource>) || undefined,
            organType,
            onRemove: handleResourceRemoveModal,
            onAssign: onResourceAssign,
          },
        });
        break;
      default:
        break;
    }
  }, [
    type,
    onOpenModal,
    canAssignResources,
    canReserveOrRemoveResources,
    resource,
    handleResourceRemoveModal,
    onResourceAssign,
    organType,
  ]);

  return {
    isLoading,
    resource,
    resourceData,
    handleAssignModal,
    handleViewResourceModal,
    handleResourceRemoveModal,
    handleCSConfirmation,
    handleSConfirmation,
  };
};
